import type { Dispatch } from 'react';

import type { SavedQueryDto } from '../../../api/saved-queries-api';
import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import type {
  ReportParameterConfig,
  TabContentAction,
} from '../../PageConfigProvider/page-reducer';
import { filterCanBePrimary } from '../../Reports/Parameters/parameters';
import { autoMapFieldsToFilters } from './auto-map-fields-to-filters';
import { deriveSelectorOverridesFromSavedQuery } from './derive-selector-overrides';
import { fieldsFromSavedQueryDto } from './saved-query-for-chart';

export function applySavedQuerySelectionToItem({
  itemId,
  query,
  reportParameters,
  dispatch,
}: {
  itemId: string;
  query: SavedQueryDto;
  reportParameters: ReportParameterConfig[];
  dispatch: Dispatch<TabContentAction>;
}): void {
  let fields = fieldsFromSavedQueryDto(query);

  const firstRequiredIndex = fields.findIndex((f) => f.required !== false);
  const hasRequiredField =
    firstRequiredIndex >= 0 && reportParameters.length > 0;

  if (hasRequiredField) {
    const primaryParam = reportParameters.find((p) => p.isPrimary);
    const paramToUse =
      primaryParam ??
      reportParameters.find((p) => filterCanBePrimary(p.filterId)) ??
      reportParameters[0];
    if (paramToUse) {
      fields = fields.map((f, i) =>
        i === firstRequiredIndex
          ? {
              ...f,
              parameterId: paramToUse.parameterId,
              value: '',
              bindingMode: 'report_parameter' as const,
            }
          : f
      );
      const needsPrimaryUpdate = !paramToUse.isPrimary;
      if (needsPrimaryUpdate) {
        const newParams = reportParameters.map((p) => ({
          ...p,
          isPrimary: p.parameterId === paramToUse.parameterId,
        }));
        dispatch({ type: 'SET_REPORT_PARAMETERS', payload: newParams });
        const filterId = paramToUse.filterId;
        if (
          filterId === FilterId.COMPANY ||
          filterId === FilterId.GEOGRAPHY ||
          filterId === FilterId.ROLE
        ) {
          dispatch({
            type: 'UPDATE_PRIMARY_FILTER',
            payload: filterId as PrimaryFilterId,
          });
        }
      }
    }
  }

  fields = autoMapFieldsToFilters(fields, reportParameters);

  const derivedOverrides = deriveSelectorOverridesFromSavedQuery(query);
  const selectorOverrides = {
    ...derivedOverrides,
    ...query.selectorOverrides,
  };

  dispatch({
    type: 'UPDATE_ITEM_SNOWFLAKE_CONFIG',
    payload: {
      id: itemId,
      snowflakeConfig: { savedQueryId: query.id, fields },
      selectorOverrides,
    },
  });
}
